import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import logger from './utils/logging';
import analyticsService from './services/analyticsService';

// Context Providers
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { AuthProvider } from './context/AuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { ReviewProvider } from './context/ReviewContext';
import { TutorialProvider } from './context/TutorialContext';

// Pages
import Home from './pages/Home';
import Gallery from './pages/Gallery';
import About from './pages/About';
import Contact from './pages/Contact';
import Policies from './pages/Policies';
import Auth from './pages/Auth';
import Product from './pages/Product/Product';
import Collection from './pages/Collection/Collection';
import Events from './pages/Events';
import EventsCalendar from './pages/EventsCalendar';
import Content from './pages/Content';
import SocialFeed from './pages/SocialFeed';
import Cart from './pages/Cart/Cart';
// Admin page removed - desktop app is used instead
// import Admin from './pages/Admin/Admin';
import CheckoutSuccess from './pages/CheckoutSuccess/CheckoutSuccess';
import CustomCheckout from './pages/Checkout/CustomCheckout';
import AfterpayReturn from './pages/Checkout/AfterpayReturn';
import CustomerProfile from './pages/Profile/CustomerProfile';
import CustomerSignup from './pages/Customer/Signup';
import CustomerLogin from './pages/Customer/Login';
import CustomerProfileNew from './pages/Customer/Profile';

// Components
import TrippyNavbar from './components/layout/TrippyNavbar';
import SlideOutCart from './components/features/SlideOutCart';
import SlideOutMobileMenu from './components/features/SlideOutMobileMenu';
import MusicPlayer from './components/features/MusicPlayer';
import WelcomeModal from './components/features/WelcomeModal';
import TutorialOverlay from './components/features/TutorialOverlay';
import RainbowChatbot from './components/features/RainbowChatbot';

// Initialize logging
logger.logComponentMount('App', { version: '2.0.0' });

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    const pageName = getPageName(location.pathname);
    analyticsService.trackPageView(pageName, location.pathname);
  }, [location]);

  return null;
}

// Helper to get readable page names
function getPageName(pathname) {
  const routes = {
    '/': 'Home',
    '/gallery': 'Gallery',
                    '/events': 'Events',
                    '/events-calendar': 'Book Events',
                    '/content': 'Content',
                    '/social': 'Zee Social Stream',
                    '/about': 'About',
                    '/contact': 'Contact',
    '/policies': 'Policies',
    '/auth': 'Auth',
    '/cart': 'Cart',
    '/checkout': 'Checkout',
        '/checkout/afterpay-return': 'Zee Afterpay Return',
        '/checkout/success': 'Checkout Success',
        '/profile': 'Profile',
        '/customer/login': 'Customer Login',
        '/customer/signup': 'Customer Signup',
        '/customer/profile': 'Customer Profile',
        // '/admin': 'Admin' // Removed - desktop app used instead
  };
  
  if (pathname.startsWith('/product/')) return 'Product Details';
  if (pathname.startsWith('/events/')) return 'Event Details';
  
  return routes[pathname] || 'Unknown Page';
}

function AppLayout({
  navLinks,
  isCartOpen,
  handleCartToggle,
  setIsCartOpen,
  isMobileMenuOpen,
  handleMobileMenuToggle,
  setIsMobileMenuOpen
}) {
  const location = useLocation();
  const isSocialPage = location.pathname.startsWith('/social');

  return (
    <div className="App">
      <AnalyticsTracker />
      <WelcomeModal />
      <TutorialOverlay />
      <TrippyNavbar 
        isCartOpen={isCartOpen} 
        setIsCartOpen={setIsCartOpen}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        handleCartToggle={handleCartToggle}
        handleMobileMenuToggle={handleMobileMenuToggle}
        navLinks={navLinks}
      />
      <SlideOutCart 
        isOpen={isCartOpen} 
        onClose={() => {
          const timestamp = new Date().toISOString();
          const functionName = 'Cart onClose';
          
          console.log('\n' + '='.repeat(80));
          console.log(`🔄 [FUNCTION CALL] ${functionName}() called`);
          console.log(`⏰ [TIMESTAMP] ${timestamp}`);
          console.log(`📍 [LOCATION] App.js:SlideOutCart onClose prop`);
          console.log('='.repeat(80));
          
          console.log(`📊 [STATE BEFORE] isCartOpen:`, isCartOpen);
          console.log(`📊 [STATE BEFORE] isMobileMenuOpen:`, isMobileMenuOpen);
          
          logger.logButtonClick('Cart Close');
          
          console.log(`📤 [DATA FLOW] setIsCartOpen(false) → Cart state: ${isCartOpen} → false`);
          setIsCartOpen(false);
          console.log(`✅ [RESULT] Cart state set to false`);
          console.log(`🔗 [LOGIC] Menu state left unchanged (independent)`);
          console.log('='.repeat(80) + '\n');
        }} 
      />
      <SlideOutMobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => {
          const timestamp = new Date().toISOString();
          const functionName = 'Menu onClose';
          
          console.log('\n' + '='.repeat(80));
          console.log(`🔄 [FUNCTION CALL] ${functionName}() called`);
          console.log(`⏰ [TIMESTAMP] ${timestamp}`);
          console.log(`📍 [LOCATION] App.js:SlideOutMobileMenu onClose prop`);
          console.log('='.repeat(80));
          
          console.log(`📊 [STATE BEFORE] isMobileMenuOpen:`, isMobileMenuOpen);
          console.log(`📊 [STATE BEFORE] isCartOpen:`, isCartOpen);
          
          logger.logButtonClick('Mobile Menu Close');
          
          console.log(`📤 [DATA FLOW] setIsMobileMenuOpen(false) → Menu state: ${isMobileMenuOpen} → false`);
          setIsMobileMenuOpen(false);
          console.log(`✅ [RESULT] Menu state set to false`);
          console.log(`🔗 [LOGIC] Cart state left unchanged (independent)`);
          console.log('='.repeat(80) + '\n');
        }}
        navLinks={navLinks}
      />
      <MusicPlayer suppressPlayback={isSocialPage} hidden={isSocialPage} />
      <RainbowChatbot />
    
      {console.log('🔍 [APP DEBUG] Rendering Routes component')}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/products" element={<Navigate to="/gallery" replace />} />
        <Route path="/products/:productId" element={<Navigate to="/gallery" replace />} />
        <Route path="/product/:slug" element={<Product />} />
        <Route path="/collection/:slug" element={<Collection />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:eventSlug" element={<Events />} />
        <Route path="/events-calendar" element={<EventsCalendar />} />
        <Route path="/photos" element={<Navigate to="/events" replace />} />
        <Route path="/photos/:eventSlug" element={<Navigate to="/events" replace />} />
        <Route path="/content" element={<Content />} />
        <Route path="/content/:contentSlug" element={<Content />} />
        <Route path="/social" element={<SocialFeed />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={(() => {
          console.log('🔍 [APP DEBUG] Rendering CustomCheckout route');
          return <CustomCheckout />;
        })()} />
        <Route path="/checkout/afterpay-return" element={<AfterpayReturn />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/profile" element={<CustomerProfile />} />
        <Route path="/customer/login" element={<CustomerLogin />} />
        <Route path="/customer/signup" element={<CustomerSignup />} />
        <Route path="/customer/profile" element={<CustomerProfileNew />} />
        {/* Music Manager moved to dashboard - Content > Music Manager */}
        {/* Admin route removed - desktop app is used instead */}
        {/* <Route path="/admin" element={<Admin />} /> */}
      </Routes>
    </div>
  );
}

function App() {
  console.log('🔍 [APP DEBUG] App component rendering');
  console.log('🔍 [APP DEBUG] React version:', React.version);
  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Define navigation links (shared between navbar and mobile menu)
  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/social', label: 'Zee Social' },
    { to: '/events', label: 'Events' },
    { to: '/events-calendar', label: 'Book Events' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
    { to: '/policies', label: 'Policies' }
  ];
  
  // DEBUG: Add hydration debugging
  useEffect(() => {
    console.log('🔍 [HYDRATION DEBUG] App component mounted on client side');
    console.log('🔍 [HYDRATION DEBUG] Window object available:', typeof window !== 'undefined');
    console.log('🔍 [HYDRATION DEBUG] Document object available:', typeof document !== 'undefined');
    console.log('🔍 [HYDRATION DEBUG] Navigator available:', typeof navigator !== 'undefined');
    console.log('🔍 [HYDRATION DEBUG] React DOM available:', typeof ReactDOM !== 'undefined');
  }, []);
  
  // Initialize app - move ALL browser API calls to useEffect
  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      console.log('🔍 [HYDRATION DEBUG] Running client-side initialization');
      
      logger.logComponentMount('App', { 
        version: '2.0.0',
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
      
      logger.log('info', 'system', 'App initialization started', {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        url: window.location.href
      });
    
      // Track app load
      logger.logAnalytics('app_loaded', {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
    } else {
      console.log('🔍 [HYDRATION DEBUG] Skipping client-side initialization - no window object');
    }
  }, []);
  
  // Log state changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timestamp = new Date().toISOString();
      
      console.log('\n' + '='.repeat(80));
      console.log(`🔄 [STATE CHANGE] App State Updated`);
      console.log(`⏰ [TIMESTAMP] ${timestamp}`);
      console.log(`📍 [LOCATION] App.js:useEffect state watcher`);
      console.log('='.repeat(80));
      console.log(`📊 [STATE] isCartOpen:`, isCartOpen);
      console.log(`📊 [STATE] isMobileMenuOpen:`, isMobileMenuOpen);
      console.log(`📊 [CONTEXT] URL:`, window.location.href);
      console.log('='.repeat(80) + '\n');
      
      logger.log('info', 'system', 'App state updated', {
        isCartOpen,
        isMobileMenuOpen,
        url: window.location.href
      });
    }
  }, [isCartOpen, isMobileMenuOpen]);

  const handleCartToggle = () => {
    const timestamp = new Date().toISOString();
    const functionName = 'handleCartToggle';
    
    console.log('\n' + '='.repeat(80));
    console.log(`🔄 [FUNCTION CALL] ${functionName}() called`);
    console.log(`⏰ [TIMESTAMP] ${timestamp}`);
    console.log(`📍 [LOCATION] App.js:${functionName}`);
    console.log('='.repeat(80));
    
    console.log(`📊 [STATE BEFORE] isCartOpen:`, isCartOpen);
    console.log(`📊 [STATE BEFORE] isMobileMenuOpen:`, isMobileMenuOpen);
    console.log(`📊 [STATE CHANGE] New cart state will be:`, !isCartOpen);
    
    logger.logButtonClick('Cart Toggle', { 
      currentState: isCartOpen, 
      newState: !isCartOpen 
    });
    
    // Only close menu when OPENING cart (not when closing)
    if (!isCartOpen && isMobileMenuOpen) {
      console.log(`🔗 [LOGIC] Cart is opening AND menu is open → Closing menu`);
      console.log(`📤 [DATA FLOW] setIsMobileMenuOpen(false) → Menu state: true → false`);
      setIsMobileMenuOpen(false);
      console.log(`✅ [RESULT] Menu state set to false`);
    } else {
      console.log(`🔗 [LOGIC] No menu state change needed`);
      if (isCartOpen) {
        console.log(`   → Cart is closing, menu stays as is`);
      } else if (!isMobileMenuOpen) {
        console.log(`   → Menu already closed`);
      }
    }
    
    console.log(`📤 [DATA FLOW] setIsCartOpen(${!isCartOpen}) → Cart state: ${isCartOpen} → ${!isCartOpen}`);
    setIsCartOpen(!isCartOpen);
    console.log(`✅ [RESULT] Cart state updated to:`, !isCartOpen);
    console.log('='.repeat(80) + '\n');
  };

  const handleMobileMenuToggle = () => {
    const timestamp = new Date().toISOString();
    const functionName = 'handleMobileMenuToggle';
    
    console.log('\n' + '='.repeat(80));
    console.log(`🔄 [FUNCTION CALL] ${functionName}() called`);
    console.log(`⏰ [TIMESTAMP] ${timestamp}`);
    console.log(`📍 [LOCATION] App.js:${functionName}`);
    console.log('='.repeat(80));
    
    console.log(`📊 [STATE BEFORE] isMobileMenuOpen:`, isMobileMenuOpen);
    console.log(`📊 [STATE BEFORE] isCartOpen:`, isCartOpen);
    console.log(`📊 [STATE CHANGE] New menu state will be:`, !isMobileMenuOpen);
    
    logger.logButtonClick('Mobile Menu Toggle', { 
      currentState: isMobileMenuOpen, 
      newState: !isMobileMenuOpen 
    });
    
    // Only close cart when OPENING menu (not when closing)
    if (!isMobileMenuOpen && isCartOpen) {
      console.log(`🔗 [LOGIC] Menu is opening AND cart is open → Closing cart`);
      console.log(`📤 [DATA FLOW] setIsCartOpen(false) → Cart state: true → false`);
      setIsCartOpen(false);
      console.log(`✅ [RESULT] Cart state set to false`);
    } else {
      console.log(`🔗 [LOGIC] No cart state change needed`);
      if (isMobileMenuOpen) {
        console.log(`   → Menu is closing, cart stays as is`);
      } else if (!isCartOpen) {
        console.log(`   → Cart already closed`);
      }
    }
    
    console.log(`📤 [DATA FLOW] setIsMobileMenuOpen(${!isMobileMenuOpen}) → Menu state: ${isMobileMenuOpen} → ${!isMobileMenuOpen}`);
    setIsMobileMenuOpen(!isMobileMenuOpen);
    console.log(`✅ [RESULT] Menu state updated to:`, !isMobileMenuOpen);
    console.log('='.repeat(80) + '\n');
  };

  return (
    <Router>
      <CustomerAuthProvider>
        <AuthProvider>
          <ReviewProvider>
            <CurrencyProvider>
              <CartProvider>
                <TutorialProvider>
                  <AppLayout
                    navLinks={navLinks}
                    isCartOpen={isCartOpen}
                    handleCartToggle={handleCartToggle}
                    setIsCartOpen={setIsCartOpen}
                    isMobileMenuOpen={isMobileMenuOpen}
                    handleMobileMenuToggle={handleMobileMenuToggle}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                  />
                </TutorialProvider>
              </CartProvider>
            </CurrencyProvider>
          </ReviewProvider>
        </AuthProvider>
      </CustomerAuthProvider>
    </Router>
  );
}

export default App;



